import { Fragment, useState } from 'react';

import { AnimatePresence } from 'framer-motion';

import { Slide } from '@components/animators/slide';
import { HideContainer } from '@components/hide-container';
import { IconArrowDown } from '@components/icons/arrow-down';
import { IconAskAI } from '@components/icons/ask-ai';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconOutput } from '@components/icons/output';
import { IconTextLong } from '@components/icons/text-long';
import { InfoBox } from '@components/info-box';
import { RadioGroup } from '@components/radio-group';
import { Separator } from '@components/separator';
import { InfoButton, Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { VariableValue } from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';

import { useWebSocket } from 'api/hooks';
import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { TimeSavedData } from 'api/journeys/requests';
import { JourneyActionDetailed } from 'api/types';
import { TimeSavedContainer } from 'ui/helpers';
import { useFeature } from 'ui/hooks';
import {
  ActionFooter,
  ActionHeader,
  ActionSidebarLoading,
  DescriptionFormField,
  SidebarProps,
  createActionForm,
  useCreateActionFormStore,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';

import {
  AskAIInfoBox,
  AskAIInfoBoxClosable,
  TemplateName,
  getOutputValue,
  getPromptTemplateOptions,
  getPromptValue,
} from './helpers';

import { FormWithUnsavedChangesPrompt } from '../../journey-builder/unsaved-changes-prompt';
import { getFieldMappingId } from '../helpers';

type ActionType = 'ask_ai';
type OutputType = 'tokens' | 'text';

type Data = {
  description: string;
  field: string;
  template: string;
  prompt: string;
  outputType: OutputType;
  output?: string[];
} & TimeSavedData;

const Form = createActionForm<Data>();

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<ActionType>(journeyActionId);
  const { t } = useScopedTranslate(sidebarLocale);

  if (isLoading || !data) {
    return <ActionSidebarLoading title={t('askAI')} icon={IconAskAI} {...props} />;
  }

  return (
    <Sidebar>
      <ActionHeader title={t('askAI')} icon={IconAskAI} {...props}>
        <InfoButton>
          <Info />
        </InfoButton>
      </ActionHeader>
      <Sidebar.Content>
        <AnimatePresence initial={false} exitBeforeEnter>
          <Slide key="form" offset={-30}>
            <FormContainer journeyActionId={journeyActionId} data={data} />
          </Slide>
        </AnimatePresence>
      </Sidebar.Content>
    </Sidebar>
  );
};

const Info = () => {
  const { t } = useScopedTranslate(sidebarLocale);
  return (
    <Stack space={4}>
      <Text font="bold16" text={t('infoTitle')} />
      <Text font="regular14" text={t('infoText')} />
      <Stack space={2} align="center">
        <div style={{ width: '100%' }}>
          <InfoBox variant="green" icon={<IconCheckmark />} text={t('infoField')} />
        </div>
        <IconArrowDown color="elementPurple50" />
        <InfoBox variant="blue" icon={<IconTextLong />} text={t('infoPrompt')} />
        <IconArrowDown color="elementPurple50" />
        <InfoBox variant="blue" icon={<IconAskAI />} text={t('infoAnalyze')} />
        <IconArrowDown color="elementPurple50" />
        <InfoBox
          variant="indigo"
          icon={<IconOutput />}
          text={t('infoOutputToken')}
          header={t('token')}
        />
        <Stack
          space={4}
          orientation="horizontal"
          justify="space-between"
          align="center"
          style={{ width: '100%' }}
        >
          <Separator />
          <Text font="semibold14" text={t('core.or')} color="textHelper" />
          <Separator />
        </Stack>
        <InfoBox
          variant="indigo"
          icon={<IconOutput />}
          text={t('infoOutputText')}
          header={t('text')}
        />
      </Stack>
    </Stack>
  );
};

const FormContainer = ({
  journeyActionId,
  data,
}: {
  journeyActionId: string;
  data: JourneyActionDetailed<'ask_ai'>;
}) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { isEnabled: hasContentAIEnabled } = useFeature('FEATURE_AI_CONTENT');
  const formStore = useCreateActionFormStore();
  const templateValue = formStore.useFieldValue('template') as TemplateName;
  const outputType = formStore.useFieldValue('outputType') as OutputType;
  const isDirty = formStore.useIsDirty();
  const formDescription = formStore.useFieldValue('description');
  const [newData, setNewData] = useState<
    { prompt: VariableValue; outputType: OutputType; output: VariableValue[] } | undefined
  >(undefined);
  const editMutation = useUpdateJourneyAction(journeyActionId);
  const fieldMappingId = getFieldMappingId(data.journey ?? '');

  const handleSave = async (data: Data) => {
    await editMutation.mutateAsync({
      description: data.description,
      isAutomatic: true,
      action: 'ask_ai',
      values: {
        field: data.field,
        template: data.template,
        prompt: data.prompt,
        outputType: data.outputType,
        output: data.output ?? [],
      },
      timeSaved: {
        days: data.days,
        hours: data.hours,
        minutes: data.minutes,
      },
    });
  };

  const variableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    excludedTypes: [],
  });

  const actionDescription = formDescription || data.description;
  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={data.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('input')}>
          <Stack space={4}>
            <AskAIInfoBoxClosable infoType="field" />
            <Form.FieldMappingField
              id="field"
              label={t('field')}
              variableGroups={variableGroups}
              defaultValue={data.values?.field}
              singleLine
              plaintext
              validators={['required']}
              info={t('fieldInfo')}
              fieldMappingId={fieldMappingId}
            />
            <AskAIInfoBox infoType="askAI" />
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('prompt')}>
          <Stack space={4}>
            <AskAIInfoBoxClosable infoType="prompt" />
            <Form.Select
              id="template"
              options={getPromptTemplateOptions()}
              label={t('template')}
              defaultValue={data.values?.template}
              // @ts-ignore
              onChange={(templateType: TemplateName) => {
                setNewData({
                  prompt: getPromptValue(templateType),
                  outputType: getOutputValue(templateType).length > 0 ? 'tokens' : 'text',
                  output: getOutputValue(templateType),
                });
              }}
              placeholder={t('selectTemplate')}
              validators={['required']}
            />
            <Fragment key={templateValue}>
              <Form.FieldMappingTextEditor
                id="prompt"
                label={t('prompt')}
                variableGroups={variableGroups}
                defaultValue={newData?.prompt ?? data.values?.prompt}
                plaintext
                validators={['required']}
                fieldMappingId={fieldMappingId}
                aiOptions={
                  hasContentAIEnabled
                    ? {
                        generateChoices: actionDescription
                          ? [
                              {
                                title: t('generateFromActionDescription'),
                                prompt: `Write an AI agent prompt with this description: ${actionDescription}`,
                              },
                            ]
                          : [],
                        threadInstructions:
                          'Transform the users input into a refined prompt intended for another AI agent. Do not generate any output beyond the prompt itself—do not write the actual story, joke, or content. Only return the improved prompt. For instance, if the user says "write a story about a robot", the refined prompt should be: "You are a creative storyteller. Write a captivating short story about a curious robot exploring a futuristic world."',
                        useWebSocket,
                      }
                    : undefined
                }
              />
            </Fragment>
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('output')} key={templateValue}>
          <Stack space={4}>
            <Form.RadioGroup
              label={t('output')}
              id="outputType"
              hideLabel
              defaultValue={newData?.outputType ?? data.values?.outputType}
            >
              <RadioGroup.Option label={t('token')} key="token" value="tokens" />
              <RadioGroup.Option label={t('text')} key="text" value="text" />
            </Form.RadioGroup>
            {outputType === 'tokens' ? (
              <>
                <Form.TokenListField
                  id="output"
                  label={t('output')}
                  hideLabel
                  defaultValue={newData?.output ?? data.values?.output}
                />
                <AskAIInfoBox infoType="outputToken" />
              </>
            ) : (
              <AskAIInfoBox infoType="outputText" />
            )}
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <TimeSavedContainer {...data.timeSaved} />
      </Stack>
      <ActionFooter action={data} isDisabled={!isDirty || editMutation.isLoading} form={Form} />
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    askAI: 'Ask AI',
    actionDetails: 'Action details',
    input: 'Input',
    prompt: 'Prompt',
    output: 'Output',
    token: 'Token',
    text: 'Text',
    template: 'Prompt templates',
    selectTemplate: 'Select template',
    field: 'Field',
    fieldInfo: 'Field to apply the prompt to',
    infoTitle: 'About Ask AI',
    infoText: 'Ask AI is a tool that helps you analyse and format text data',
    infoPrompt: 'Create the prompt for AI to process selected field',
    infoField: 'Select field/input you want to analyse',
    infoAnalyze: 'AI Analyses the input based on your specifications and guidelines',
    infoOutputToken:
      'AI outputs a token result based on your parameters. This token can be used in various ways, e.g. as a condition for branches',
    infoOutputText:
      'AI outputs a summarised text you can supplement your data with or help you analyse and review content',
    generateFromActionDescription: 'Generate from action description',
  },
};
